/**
 * 66.7 C1, the cover lens (ARCHITECTURE §5.15; decision 144, the owner: *"they break cover constantly and charge across
 * open fields"*): how much of the time a bot is open to an enemy, moving and standing, and how it dies — moving or not,
 * and how far from the nearest prop. Measured against the truth (every live enemy, not what the team knows), with the
 * bots' own sight rule: the viewer's range under the weather, the ground and smoke (`personLineOfSight`), props
 * (`segmentCovered`), and (66.7, foliageSight on) a crown deep enough (`crownHides`), to the body at its stance. Kills from cover and the shooter's height are the engagements'
 * (actionDuel engagementCollector). Measurement only: nothing here feeds a decision.
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import { vecNormalize, vecSub, vecDot, vecLength } from '../math';
import { dacos } from '../core/dmath';
import { personSetOf } from '../core/scaleProfile';
import { personEyePoint, personLineOfSight } from '../core/fineTerrain';
import { segmentCovered } from '../core/bullets';
import { footprintObstacles, personMover } from '../core/hits';
import { weatherEffectHere } from '../core/weather';
import { weatherSees } from '../core/perception';
import { crownHides } from '../core/foliage';
import { PHYS_DT } from '../constants';

/** A unit faster than this share of its walk between two samples is moving. */
export const COVER_LENS_MOVING = 0.25;
/** A jump of more than this many walks between samples is a respawn or a ride, not a step: not counted as moving. */
const TELEPORT_WALKS = 4;
/** A death this close (person lengths) to a prop's edge had cover at hand. */
export const DEATH_NEAR_PROP = 1.5;

export interface CoverLensTeam {
  /** Living bot-samples, and the share of them spent moving. */
  samples: number;
  moving: number;
  /** Share of the moving samples, and of the still ones, in which some live enemy could see the body. */
  openMoving: number;
  openStill: number;
  /** Deaths; the share that came while moving; the mean gap (person lengths) from the body to the nearest prop's edge, and the share within DEATH_NEAR_PROP of one. */
  deaths: number;
  diedMoving: number;
  deathPropGap: number | null;
  diedNearProp: number;
}

export interface CoverLensSummary { all: CoverLensTeam; byTeam: Record<number, CoverLensTeam> }

const arc = (a: Vec3, b: Vec3, r: number) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * r;

interface Tally { samples: number; moving: number; openMoving: number; openStill: number; deaths: number; diedMoving: number; gaps: number[] }
const blank = (): Tally => ({ samples: 0, moving: 0, openMoving: 0, openStill: 0, deaths: 0, diedMoving: 0, gaps: [] });

/** Whether some live enemy of `u` could see its body now (the bots' sight rule, from each enemy's eye). */
function openToEnemy(state: GameState, u: PlayerState, seconds: number): boolean {
  const ps = personSetOf(state);
  const body = personEyePoint(u, state.planets, ps, ps.hitboxCenter);
  for (const e of state.players) {
    if (!e.alive || e.teamIndex === u.teamIndex || e.planetIndex !== u.planetIndex) continue;
    const eye = personEyePoint(e, state.planets, ps, ps.losEyeHeight);
    const range = weatherEffectHere(state, e.planetIndex, e.surfaceDir, seconds).sight * ps.k;
    if (vecLength(vecSub(body, eye)) > range) continue;
    if (!weatherSees(state, e.planetIndex, eye, body, seconds, range, u.planetIndex)) continue;
    if (!personLineOfSight(eye, body, state)) continue;
    if (segmentCovered(eye, body, state, undefined, ps.coverSampleStep)) continue;
    if (crownHides(state, u.planetIndex, eye, body, u.airborne ? undefined : u.stance)) continue;   // (as the bots' looks: foliageSight)
    return true;
  }
  return false;
}

export function coverLensCollector() {
  const teams = new Map<number, Tally>();
  const tallyOf = (t: number) => { let x = teams.get(t); if (!x) { x = blank(); teams.set(t, x); } return x; };
  const last = new Map<number, { dir: Vec3; tick: number; moving: boolean }>();
  return {
    /** One look at every living unit (call every eighth of a second or so). */
    sample(state: GameState, tick: number): void {
      const ps = personSetOf(state);
      for (const u of state.players) {
        if (!u.alive) { last.delete(u.id); continue; }
        const planet = state.planets[u.planetIndex];
        const prev = last.get(u.id);
        let moving = false;
        if (prev) {
          const walked = arc(prev.dir, u.surfaceDir, planet.radius) / Math.max(1e-9, ps.walkSpeed * (tick - prev.tick) * PHYS_DT);
          moving = walked > COVER_LENS_MOVING && walked < TELEPORT_WALKS;
        }
        last.set(u.id, { dir: { ...u.surfaceDir }, tick, moving });
        if (!prev || u.airborne || u.seatPos) continue;   // a fresh unit, or one in the air or in a seat: not a walker's sample
        const t = tallyOf(u.teamIndex);
        t.samples++;
        const open = openToEnemy(state, u, tick * PHYS_DT);
        if (moving) { t.moving++; if (open) t.openMoving++; } else if (open) t.openStill++;
      }
    },
    /** A unit died (call as its death is reported: its body is still where it fell). */
    death(state: GameState, victimId: number): void {
      const v = state.players.find(p => p.id === victimId);
      if (!v) return;
      const t = tallyOf(v.teamIndex);
      t.deaths++;
      if (last.get(v.id)?.moving) t.diedMoving++;
      const ps = personSetOf(state);
      const planet = state.planets[v.planetIndex];
      let gap = Infinity;
      for (const o of footprintObstacles(state, v.planetIndex, personMover(ps)).list) gap = Math.min(gap, Math.max(0, arc(o.dir, v.surfaceDir, planet.radius) - o.radius));
      if (Number.isFinite(gap)) t.gaps.push(gap / ps.k);   // person lengths, as the engagements' range
      last.delete(v.id);
    },
    summary(): CoverLensSummary {
      const out = (x: Tally): CoverLensTeam => {
        const still = x.samples - x.moving;
        return {
          samples: x.samples, moving: x.samples ? x.moving / x.samples : 0,
          openMoving: x.moving ? x.openMoving / x.moving : 0, openStill: still ? x.openStill / still : 0,
          deaths: x.deaths, diedMoving: x.deaths ? x.diedMoving / x.deaths : 0,
          deathPropGap: x.gaps.length ? x.gaps.reduce((a, b) => a + b, 0) / x.gaps.length : null,
          diedNearProp: x.gaps.length ? x.gaps.filter(g => g <= DEATH_NEAR_PROP).length / x.gaps.length : 0,
        };
      };
      const all = blank();
      for (const x of teams.values()) { all.samples += x.samples; all.moving += x.moving; all.openMoving += x.openMoving; all.openStill += x.openStill; all.deaths += x.deaths; all.diedMoving += x.diedMoving; all.gaps.push(...x.gaps); }
      return { all: out(all), byTeam: Object.fromEntries([...teams].sort((a, b) => a[0] - b[0]).map(([t, x]) => [t, out(x)])) };
    },
  };
}
